import axios from 'axios';

/**
 * localStorage stringifies whatever it is given, so `setItem('accessToken', undefined)`
 * stores the four characters "undefined" — a truthy string that every `if (token)` guard
 * happily forwards as `Authorization: Bearer undefined`. Treat those sentinels as "no
 * token" and evict them, so browsers already carrying a poisoned value heal on next load.
 */
const TOKEN_SENTINELS = new Set(['undefined', 'null', 'NaN', '']);

export const readStoredToken = (tokenName) => {
  const raw = localStorage.getItem(tokenName);
  if (raw === null) {
    return null;
  }
  if (TOKEN_SENTINELS.has(raw.trim())) {
    localStorage.removeItem(tokenName);
    localStorage.removeItem(`${tokenName}_domain`);
    return null;
  }
  return raw;
};

// Function to get token considering domain restrictions
export const getTokenForDomain = (tokenName) => {
  const token = readStoredToken(tokenName);
  const tokenDomain = localStorage.getItem(`${tokenName}_domain`);

  // If token exists and either no domain restriction or domain matches current
  if (token) {
    const currentDomain = window.location.hostname;
    // Allow localhost and local development, or no domain restriction, or matching domain
    if (!tokenDomain || tokenDomain === currentDomain || currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
      return token;
    }
  }
  return null;
};

/**
 * Stores a token only when there is a real one to store. A missing value clears the slot
 * instead of writing the string "undefined" — since the login flow moved to authorization
 * codes, verifyOtp deliberately returns no accessToken/refreshToken, so this is the normal
 * path, not an error.
 */
export const storeToken = (tokenName, tokenValue, domain) => {
  if (tokenValue === null || tokenValue === undefined || String(tokenValue).trim() === '') {
    localStorage.removeItem(tokenName);
    localStorage.removeItem(`${tokenName}_domain`);
    return false;
  }
  localStorage.setItem(tokenName, tokenValue);
  if (domain !== undefined && domain !== null) {
    localStorage.setItem(`${tokenName}_domain`, domain);
  }
  return true;
};

export const setAuthToken = () => {
  const token = getTokenForDomain('accessToken');
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export class Authentication {
  isAuthenticated() {
    const token = getTokenForDomain('accessToken');
    return !!token;
  }
}
